import { Outlet, type RouteObject } from 'react-router'

import { PageWaveLoader } from '~/shared/components/PageWaveLoader'
import { LogoutRoute } from '~/shell/auth/LogoutRoute'
import { RequireAuth } from '~/shell/auth/RequireAuth'
import { ForcedSessionReplay } from '~/shell/integrations/datadog/ForcedSessionReplay'
import {
  LaunchFailedPage,
  LaunchNotAuthorizedPage,
} from '~/shell/pages/launch/LaunchNotice'
import { LaunchPage } from '~/shell/pages/launch/LaunchPage'
import { UnauthorizedPage } from '~/shell/pages/unauthorized/UnauthorizedPage'

import { appEntryRoutes } from './appEntryRoutes'
import { AppFallback } from './AppFallback'
import { Bootstrap } from './Bootstrap'
import { CustomerLayout } from './CustomerLayout'
import { DocumentTitle } from './DocumentTitle'
import { mountedAppRoutes } from './registry'
import { RethrowRouteError } from './RethrowRouteError'

/**
 * The suite's route table: the shell's own routes plus every registered app's, mounted.
 *
 * `DocumentTitle` sits at the root beside the outlet, so it observes every route (including the ones
 * that belong to no app, the login wait, and the launch) while the guard keeps to guarding.
 *
 * `RequireAuth` is the pathless layout guarding everything that needs a session: unauthenticated users
 * are redirected to Auth0 login before any child renders. Its pathless child mounts `ForcedSessionReplay`
 * beside every guarded route, since that needs the flags the guard resolves. Beneath it:
 *   - The index route runs the post-auth `Bootstrap`, which redirects to the
 *     right customer-scoped URL (or to `/unauthorized`).
 *   - `c/:customer` is the customer-scoped layout (`CustomerLayout`) that renders
 *     the app header above whichever app is mounted below it. Each app owns the
 *     paths beneath its own segment and contributes them through the app registry,
 *     which is the only thing the shell knows about apps.
 *   - `unauthorized` is the no-access page. It belongs to the shell rather than to
 *     any app: `Bootstrap` and `CustomerLayout` both redirect here, and a session
 *     with no entitlements has no app to render it.
 *   - One entry path per app (`/arc`, `/scout`), last of the guarded routes: a customer-less URL
 *     that resolves which customer to land in and redirects into the app there.
 *
 * `launch` sits *outside* the guard, because an EMR launch arrives with no session at all: it is the
 * step that establishes one (Atlas ADR-0027), and guarding it would send a clinician to an Auth0 login
 * inside the EHR. Its three paths are the redirect targets of the atlas service's `EMR_LAUNCH_*_URL`
 * settings, spelled in `pages/launch/paths.ts`.
 *
 * `/logout` sits outside it too, and outside the root route as well: behind the guard, an
 * unauthenticated hit would loop through Universal Login and straight back out again. See
 * `LogoutRoute`.
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <>
        <DocumentTitle />
        <Outlet />
      </>
    ),
    HydrateFallback: PageWaveLoader,
    errorElement: <RethrowRouteError />,
    children: [
      // First, so an app id can never shadow the launch surface below it.
      {
        path: 'launch',
        children: [
          {
            index: true,
            element: <LaunchPage />,
          },
          {
            path: 'error',
            element: <LaunchFailedPage />,
          },
          {
            path: 'unauthorized',
            element: <LaunchNotAuthorizedPage />,
          },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: (
              <>
                <ForcedSessionReplay />
                <Outlet />
              </>
            ),
            children: [
              {
                element: <Bootstrap />,
                index: true,
              },
              {
                path: 'c/:customer',
                element: <CustomerLayout />,
                children: [
                  ...mountedAppRoutes(),
                  // Resolve missing and unknown app routes through the same landing flow so they always
                  // choose the same destination (Atlas ADR-0015).
                  {
                    index: true,
                    element: <AppFallback />,
                  },
                  {
                    path: '*',
                    element: <AppFallback />,
                  },
                ],
              },
              {
                path: 'unauthorized',
                element: <UnauthorizedPage />,
              },
              // Last, so an app id can never shadow one of the shell's own paths above. Guarded like the
              // rest, because each entry path runs the bootstrap and so needs the session's claims.
              ...appEntryRoutes(),
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/logout',
    element: <LogoutRoute />,
  },
]
